import type { ComponentProps } from 'react'
import type { Product } from '@/lib/services/products/products.types'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { ProductAddOnStorefrontVisibility } from '@/lib/enums/ProductAddOnStorefrontVisibility'
import { ProductStorefrontVisibility } from '@/lib/enums/ProductStorefrontVisibility'
import { productNeedsVisibleCollection } from '@/lib/services/products/products.visibility'
import { cn, getEnumDisplayName } from '@/lib/utils'

type BadgeTone = Pick<ComponentProps<typeof Badge>, 'variant'>
type VisibilityProduct = Partial<
  Pick<
    Product,
    | 'storefront_visibility'
    | 'add_on_storefront_visibility'
    | 'is_add_on'
    | 'is_add_on_only'
    | 'collections'
  >
>
const storefrontVisibilityBadgeTone = {
  [ProductStorefrontVisibility.Always.Value]: {
    variant: 'success',
  },
  [ProductStorefrontVisibility.PublishedCollection.Value]: {
    variant: 'info',
  },
  [ProductStorefrontVisibility.Hidden.Value]: {
    variant: 'muted',
  },
} satisfies Record<Product['storefront_visibility'], BadgeTone>

const addOnStorefrontVisibilityBadgeTone = {
  [ProductAddOnStorefrontVisibility.Always.Value]: {
    variant: 'success',
  },
  [ProductAddOnStorefrontVisibility.Hidden.Value]: {
    variant: 'muted',
  },
} satisfies Record<Product['add_on_storefront_visibility'], BadgeTone>

function productNeedsVisibleCollectionOverride(
  product: VisibilityProduct | null | undefined,
  hasVisibleCollection: boolean | undefined
) {
  if (typeof hasVisibleCollection !== 'boolean') {
    return productNeedsVisibleCollection(product)
  }

  return (
    product?.storefront_visibility === ProductStorefrontVisibility.PublishedCollection.Value &&
    !hasVisibleCollection
  )
}

function getProductVisibilityLabel(visibility: Product['storefront_visibility'] | undefined) {
  if (!visibility) {
    return 'Unknown'
  }

  return getEnumDisplayName(ProductStorefrontVisibility, visibility)
}

function getAddOnVisibilityLabel(visibility: Product['add_on_storefront_visibility'] | undefined) {
  if (!visibility) {
    return 'Unknown'
  }

  return getEnumDisplayName(ProductAddOnStorefrontVisibility, visibility)
}

export default function ProductStorefrontVisibilityBadge({
  product,
  hasVisibleCollection,
  showIcon = true,
  size,
  stackClassName,
}: {
  product?: VisibilityProduct | null
  hasVisibleCollection?: boolean
  showIcon?: boolean
  size?: ComponentProps<typeof Badge>['size']
  stackClassName?: string
}) {
  const visibility = product?.storefront_visibility
  const addOnVisibility =
    product?.add_on_storefront_visibility ?? ProductAddOnStorefrontVisibility.Hidden.Value
  const needsVisibleCollection = productNeedsVisibleCollectionOverride(
    product,
    hasVisibleCollection
  )
  const tone: BadgeTone = needsVisibleCollection
    ? {
        variant: 'warning',
      }
    : (storefrontVisibilityBadgeTone[visibility ?? ProductStorefrontVisibility.Hidden.Value] ??
      storefrontVisibilityBadgeTone[ProductStorefrontVisibility.Hidden.Value])
  const productBadge = (
    <Badge variant={tone.variant} size={size ?? 'status'} indicator={showIcon}>
      {product?.is_add_on ? 'Product: ' : null}
      {getProductVisibilityLabel(visibility)}
    </Badge>
  )
  const visibleProductBadge = needsVisibleCollection ? (
    <Tooltip>
      <TooltipTrigger asChild>{productBadge}</TooltipTrigger>
      <TooltipContent>Not currently in a visible collection.</TooltipContent>
    </Tooltip>
  ) : (
    productBadge
  )
  const addOnTone =
    addOnStorefrontVisibilityBadgeTone[addOnVisibility] ??
    addOnStorefrontVisibilityBadgeTone[ProductAddOnStorefrontVisibility.Hidden.Value]
  const addOnBadge = (
    <Badge variant={addOnTone.variant} size={size ?? 'status'} indicator={showIcon}>
      Add-on: {getAddOnVisibilityLabel(addOnVisibility)}
    </Badge>
  )

  if (product?.is_add_on) {
    return (
      <span className={cn('flex flex-wrap gap-1.5', stackClassName)}>
        {product?.is_add_on_only ? null : visibleProductBadge}
        {addOnBadge}
      </span>
    )
  }

  if (!needsVisibleCollection) {
    return productBadge
  }

  return visibleProductBadge
}
